import { ByteData, QRByteSegment, QRUtil } from './qr_support'
import {
  FcArea,
  FcElement,
  FcErrorCorrectionLevel,
  FcGrid,
  FcPositionProperty
} from '@dtx-company/flowcode-generator-types/src'

export function fcBuildFlowcode(
  data: string,
  grid: FcGrid,
  paddedCode?: boolean,
  usePaddedPattern?: boolean
): void {
  const byteData = new QRByteSegment(data)
  if (paddedCode) {
    grid.errorCorrectionLevel = FcErrorCorrectionLevel.L
    grid.typeNumber = 5
  } else {
    grid.typeNumber = QRUtil.getTypeNumber(data, grid.errorCorrectionLevel)
    if (grid.overrideTypeNumber && grid.overrideTypeNumber > grid.typeNumber) {
      grid.typeNumber = grid.overrideTypeNumber
    }
  }
  let padNumbers: number[] = []
  if (usePaddedPattern) {
    padNumbers = getPadNumbers(grid)
  }
  const cache = QRUtil.createData(grid.typeNumber, grid.errorCorrectionLevel, byteData, padNumbers)
  populateGrid(grid, cache, false, paddedCode ? 0 : getBestMaskPattern(grid, cache))
}

function getPadNumbers(grid: FcGrid): number[] {
  const padNumbers: number[] = []
  const length = grid.totalModuleCount
  for (let y = 0; y < length; y++) {
    for (let x = 0; x < length; x++) {
      const module = grid.modules[y][x]
      if (module.isPad) {
        const bitSet = module.mask ? !module.set : module.set
        if (bitSet) {
          padNumbers[module.byteIndex!] |= 1 << module.bitIndex!
        }
      }
    }
  }
  let skip = 0
  while (padNumbers[skip] === undefined) {
    skip++
  }
  return padNumbers.slice(skip)
}

function setupPositionProbePattern(datagrid: FcGrid, row: number, col: number): void {
  for (let r = -1; r <= 7; r++) {
    if (row + r <= -1 || datagrid.dataModuleCount <= row + r) continue
    for (let c = -1; c <= 7; c++) {
      if (col + c <= -1 || datagrid.dataModuleCount <= col + c) continue
      if (
        (0 <= r && r <= 6 && (c === 0 || c === 6)) ||
        (0 <= c && c <= 6 && (r === 0 || r === 6)) ||
        (2 <= r && r <= 4 && 2 <= c && c <= 4)
      ) {
        datagrid.setData(row + r, col + c, true, FcElement.POSITION)
      } else {
        datagrid.setData(row + r, col + c, false, FcElement.POSITION)
      }
    }
  }
}

function setupTimingPattern(grid: FcGrid): void {
  for (let r = 8; r < grid.dataModuleCount - 8; r++) {
    if (grid.getDataModule(r, 6).type !== FcElement.UNSET) {
      continue
    }
    grid.setData(r, 6, r % 2 === 0, FcElement.TIMING)
  }
  for (let c = 8; c < grid.dataModuleCount - 8; c++) {
    if (grid.getDataModule(6, c).type !== FcElement.UNSET) {
      continue
    }
    grid.setData(6, c, c % 2 === 0, FcElement.TIMING)
  }
}

function setupPositionAdjustPattern(grid: FcGrid): void {
  const pos = QRUtil.getPatternPosition(grid.typeNumber)
  for (let i = 0; i < pos.length; i++) {
    for (let j = 0; j < pos.length; j++) {
      const row = pos[i]
      const col = pos[j]
      if (grid.getDataModule(row, col).type !== FcElement.UNSET) {
        continue
      }
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          if (r === -2 || r === 2 || c === -2 || c === 2 || (r === 0 && c === 0)) {
            grid.setData(row + r, col + c, true, FcElement.ALIGNMENT)
          } else {
            grid.setData(row + r, col + c, false, FcElement.ALIGNMENT)
          }
        }
      }
    }
  }
}

function getBestMaskPattern(grid: FcGrid, cache: Array<ByteData>): number {
  let minLostPoint = 0
  let pattern = 0
  for (let i = 0; i < 8; i++) {
    populateGrid(grid, cache, true, i)
    const lostPoint = getLostPoint(grid)
    if (i === 0 || minLostPoint > lostPoint) {
      minLostPoint = lostPoint
      pattern = i
    }
  }
  return pattern
}

function mapData(grid: FcGrid, data: Array<ByteData>, maskPattern: number): void {
  let inc = -1
  let row = grid.dataModuleCount - 1
  let bitIndex = 7
  let byteIndex = 0
  for (let col = grid.dataModuleCount - 1; col > 0; col -= 2) {
    if (col === 6) col--
    // eslint-disable-next-line no-constant-condition
    while (true) {
      for (let c = 0; c < 2; c++) {
        if (grid.getDataModule(row, col - c).type === FcElement.UNSET) {
          let dark = false
          let rsGroup = -1
          let rsAllowedErrors = -1
          let isPad = false
          if (byteIndex < data.length) {
            dark = ((data[byteIndex].data >>> bitIndex) & 1) === 1
            rsGroup = data[byteIndex].rsGroup
            isPad = data[byteIndex].isPad || false
            rsAllowedErrors = data[byteIndex].rsAllowedErrors
          }

          const mask = QRUtil.getMask(maskPattern, row, col - c)
          if (mask) {
            dark = !dark
          }
          grid.setData(
            row,
            col - c,
            dark,
            FcElement.DATA,
            undefined,
            byteIndex,
            rsGroup,
            rsAllowedErrors,
            isPad,
            bitIndex,
            mask
          )
          bitIndex--
          if (bitIndex === -1) {
            byteIndex++
            bitIndex = 7
          }
        }
      }
      row += inc
      if (row < 0 || grid.dataModuleCount <= row) {
        row -= inc
        inc = -inc
        break
      }
    }
  }
}

function setupTypeNumber(grid: FcGrid, test: boolean): void {
  const bits = QRUtil.getBCHTypeNumber(grid.typeNumber)
  for (let i = 0; i < 18; i++) {
    const mod = !test && ((bits >> i) & 1) === 1
    grid.setData(Math.floor(i / 3), (i % 3) + grid.dataModuleCount - 8 - 3, mod, FcElement.TYPEINFO)
  }
  for (let i = 0; i < 18; i++) {
    const mod = !test && ((bits >> i) & 1) === 1
    grid.setData((i % 3) + grid.dataModuleCount - 8 - 3, Math.floor(i / 3), mod, FcElement.TYPEINFO)
  }
}

function setupTypeInfo(grid: FcGrid, test: boolean, maskPattern: number): void {
  const data = (grid.errorCorrectionLevel << 3) | maskPattern
  const bits = QRUtil.getBCHTypeInfo(data)
  for (let i = 0; i < 15; i++) {
    const mod = !test && ((bits >> i) & 1) === 1
    if (i < 6) {
      grid.setData(i, 8, mod, FcElement.TYPEINFO)
    } else if (i < 8) {
      grid.setData(i + 1, 8, mod, FcElement.TYPEINFO)
    } else {
      grid.setData(grid.dataModuleCount - 15 + i, 8, mod, FcElement.TYPEINFO)
    }
  }
  for (let i = 0; i < 15; i++) {
    const mod = !test && ((bits >> i) & 1) === 1
    if (i < 8) {
      grid.setData(8, grid.dataModuleCount - i - 1, mod, FcElement.TYPEINFO)
    } else if (i < 9) {
      grid.setData(8, 15 - i - 1 + 1, mod, FcElement.TYPEINFO)
    } else {
      grid.setData(8, 15 - i - 1, mod, FcElement.TYPEINFO)
    }
  }
  grid.setData(grid.dataModuleCount - 8, 8, !test, FcElement.TYPEINFO)
}

function populateGrid(
  grid: FcGrid,
  cache: Array<ByteData>,
  test: boolean,
  maskPattern: number
): void {
  grid.initialize(grid.typeNumber * 4 + 17)
  setupPositionProbePattern(grid, 0, 0)
  setupPositionProbePattern(grid, grid.dataModuleCount - 7, 0)
  setupPositionProbePattern(grid, 0, grid.dataModuleCount - 7)
  setupPositionAdjustPattern(grid)
  setupTimingPattern(grid)
  setupTypeInfo(grid, test, maskPattern)
  if (grid.typeNumber >= 7) {
    setupTypeNumber(grid, test)
  }

  mapData(grid, cache, maskPattern)
  grid.setPositionElementArea(FcPositionProperty.TOP_LEFT, new FcArea(0, 0, 7, 7))
  grid.setPositionElementArea(
    FcPositionProperty.TOP_RIGHT,
    new FcArea(grid.dataModuleCount - 7, 0, 7, 7)
  )
  grid.setPositionElementArea(
    FcPositionProperty.BOTTOM_LEFT,
    new FcArea(0, grid.dataModuleCount - 7, 7, 7)
  )
  grid.applyTransformation()
}

function getLostPoint(grid: FcGrid): number {
  const moduleCount = grid.dataModuleCount
  let lostPoint = 0
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      let sameCount = 0
      const dark = grid.isDataSet(row, col)
      for (let r = -1; r <= 1; r++) {
        if (row + r < 0 || moduleCount <= row + r) {
          continue
        }
        for (let c = -1; c <= 1; c++) {
          if (col + c < 0 || moduleCount <= col + c) {
            continue
          }
          if (r === 0 && c === 0) {
            continue
          }
          if (dark === grid.isDataSet(row + r, col + c)) {
            sameCount++
          }
        }
      }
      if (sameCount > 5) {
        lostPoint += 3 + sameCount - 5
      }
    }
  }
  for (let row = 0; row < moduleCount - 1; row++) {
    for (let col = 0; col < moduleCount - 1; col++) {
      let count = 0
      if (grid.isDataSet(row, col)) count++
      if (grid.isDataSet(row + 1, col)) count++
      if (grid.isDataSet(row, col + 1)) count++
      if (grid.isDataSet(row + 1, col + 1)) count++
      if (count === 0 || count === 4) {
        lostPoint += 3
      }
    }
  }
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount - 6; col++) {
      if (
        grid.isDataSet(row, col) &&
        !grid.isDataSet(row, col + 1) &&
        grid.isDataSet(row, col + 2) &&
        grid.isDataSet(row, col + 3) &&
        grid.isDataSet(row, col + 4) &&
        !grid.isDataSet(row, col + 5) &&
        grid.isDataSet(row, col + 6)
      ) {
        lostPoint += 40
      }
    }
  }
  for (let col = 0; col < moduleCount; col++) {
    for (let row = 0; row < moduleCount - 6; row++) {
      if (
        grid.isDataSet(row, col) &&
        !grid.isDataSet(row + 1, col) &&
        grid.isDataSet(row + 2, col) &&
        grid.isDataSet(row + 3, col) &&
        grid.isDataSet(row + 4, col) &&
        !grid.isDataSet(row + 5, col) &&
        grid.isDataSet(row + 6, col)
      ) {
        lostPoint += 40
      }
    }
  }
  let darkCount = 0
  for (let col = 0; col < moduleCount; col++) {
    for (let row = 0; row < moduleCount; row++) {
      if (grid.isDataSet(row, col)) {
        darkCount++
      }
    }
  }
  const ratio = Math.abs((100 * darkCount) / moduleCount / moduleCount - 50) / 5
  lostPoint += ratio * 10
  return lostPoint
}
